/**
 * @module Error Boundary
 * @description Component to display when app crashes
 */

"use client";

import { Component as ReactComponent } from "react";
import Image from "next/image";
import PhoneButton from "@/blocks/Buttons/PhoneButton";
import { trackEvent } from "@/js/helpers/tracking";
import { fastly } from "@/js/utils/helpers";

/**
 * @class ErrorBoundary
 * @description React Error Boundary that catches JavaScript errors anywhere in the child component tree
 */
class ErrorBoundary extends ReactComponent {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({
      hasError: true,
      error,
      errorInfo,
    });

    const errorName = error?.name || "UnknownError";
    const errorMessage = error?.message || "Error unknown";
    const errorStack =
      error?.stack?.split("\n")[0] || "No stack trace available";

    const data = {
      elementType: "PAGE",
      position: "ERROR BOUNDARY",
      name: `${errorName}`,
      text: `Error Boundary: ${errorMessage} \n${errorStack}`,
    };

    trackEvent({ action: "elementViewed", data });
  }

  render() {
    if (this.state.hasError) {
      return (
        <>
          <div className="component">
            <div className="l-container">
              <Image
                alt="Frontier Logo"
                src="/site-logo-rebrand_red.svg"
                loading="lazy"
                loader={fastly}
                width="32"
                height="32"
              />
              <h3>There's a problem loading this page</h3>
              <p>
                There's a technical problem that has prevented this page from
                loading. Call our experts to complete your order.
              </p>
              <PhoneButton position="ERROR BOUNDARY" />
              {process.env.NODE_ENV === "development" && (
                <details style={{ whiteSpace: "pre-wrap", marginTop: "20px" }}>
                  <summary>Error Details (Development Only)</summary>
                  <p>
                    <strong>Error:</strong>{" "}
                    {this.state.error && this.state.error.toString()}
                  </p>
                  <p>
                    <strong>Stack Trace:</strong>
                  </p>
                  <pre>{this.state.errorInfo.componentStack}</pre>
                </details>
              )}
            </div>
          </div>
        </>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
